import React, { useMemo } from 'react';
import {
  Persona,
  ProgressBar,
  mergeClasses,
} from '@fluentui/react-components';
import { type Activity } from 'botframework-directlinejs';
import { BaseMessage } from '@bingads-webui/ads-copilot-ui';
import { LiveAgentState, type ActivityRendererProps } from '@bingads-webui-react/msa-copilot-common';
import { useI18nContext } from '@bingads-webui/copilot-i18n';

import { LiveAgentPostChatSurvey } from './live-agent-post-chat-survey';
import { useLiveAgentStyles } from './styles';

const LiveAgentConnectionProgress: React.FC<Pick<ActivityRendererProps, | 'logger'>> = React.memo(({ logger }) => {
  const styles = useLiveAgentStyles();
  const i18n = useI18nContext()!;

  logger?.trace('Copilot - Live Agent: Connection progress card');

  return (
    <BaseMessage className={mergeClasses('live-agent-card-progress', styles.liveAgentCard)}>
      {i18n.getString(_TL_('Please wait while we connect you to support.'))}
      <ProgressBar
        style={{ marginTop: 8 }}
        aria-label={i18n.getString(_TL_('Connecting to support in progress'))}
      />
    </BaseMessage>
  );
});

const LiveAgentNote: React.FC<Pick<ActivityRendererProps, | 'logger'>> = React.memo(({ logger }) => {
  const styles = useLiveAgentStyles();
  const i18n = useI18nContext()!;

  logger?.trace('Copilot - Live Agent: Connection with agent message');

  return (
    <BaseMessage className={mergeClasses('live-agent-card-note', styles.liveAgentCardNote)}>
      <div className={mergeClasses('live-agent-card-note-content', styles.liveAgentCardNoteContent)}>
        {i18n.getString(_TL_('We\'ve connected you with a member of our Support team.'))}
      </div>
    </BaseMessage>
  );
});

export const LiveAgentInteractionCard: React.FC<Pick<ActivityRendererProps, 'activity' | 'logger' | 'params'>> = React.memo(({ logger, activity, params }) => {
  const styles = useLiveAgentStyles();
  const i18n = useI18nContext()!;

  const personaDetails = {
    name: activity.from.name,
    secondaryText: i18n.getString(_TL_('Microsoft Advertising Support')),
  };

  const activityText = useMemo(() => (activity.type === 'message' ? activity.text : undefined), [activity]);

  if (activityText === LiveAgentState.AGENT_ASSIGNMENT_READY) {
    const isAgentConnectedOrChatEnded = params.currentActivities?.some((act: Activity) => {
      if (act.type === 'message') {
        const message = act.text || '';
        return [LiveAgentState.AGENT_ACCEPTED, LiveAgentState.AGENT_END_CONVERSATION].includes(message);
      }
      return false;
    });

    // Show the card only if agent agent has not accepted or chat has not ended
    if (!isAgentConnectedOrChatEnded) {
      return <LiveAgentConnectionProgress logger={logger} />;
    }
    return null;
  }
  if (activityText === LiveAgentState.AGENT_ACCEPTED) {
    return <LiveAgentNote logger={logger} />;
  }
  if (activityText === LiveAgentState.AGENT_END_CONVERSATION) {
    logger?.trace('Copilot - Live Agent: Connection ended message');
    return (
      <div className={mergeClasses(styles.liveAgentCenterContent, 'live-agent-center-content')}>
        {i18n.getString(_TL_('Live agent chat ended.'))}
      </div>
    );
  }
  if (activityText === LiveAgentState.AGENT_POST_CHAT_SURVEY) {
    logger?.trace('Copilot - Live Agent: Post chat survey card');
    return (
      <LiveAgentPostChatSurvey
        logger={logger}
        styles={styles}
        activity={activity}
      />
    );
  }

  return (
    <BaseMessage className={mergeClasses('live-agent-card', styles.liveAgentCard)}>
      {activity.from.name !== 'System' && <Persona style={{ marginBottom: 8 }} size="medium" {...personaDetails} />}
      <div className={mergeClasses('live-agent-card-content', styles.liveAgentCardContent)}>
        {activityText}
      </div>
    </BaseMessage>
  );
});
